import React from 'react';
import { Rnd } from 'react-rnd';
import { FaTrash, FaArrowUp, FaArrowDown, FaCopy, FaPhone, FaImage, FaPalette, FaCamera } from 'react-icons/fa';
import { useEditorStore } from '../../store/editorStore';
import { sectionTypes, defaultSectionStyles, blockTypes, gallerySettings, frameStyles } from '../../config/constants';
import ImageEditorModal from '../modals/ImageEditorModal';
import './Section.css';

const Section = ({ section }) => {
  const {
    updateSection, 
    setSelectedSection, 
    selectedSectionId, 
    grid, 
    blocks,
    showSectionOverlay,
    updateGalleryItem,
    removeSection,
    duplicateSection,
    moveSectionOrder
  } = useEditorStore();

  const isSelected = selectedSectionId === section.id;
  const [selectedItemId, setSelectedItemId] = React.useState(null);
  const [resizing, setResizing] = React.useState(null);
  const [dragging, setDragging] = React.useState(null);
  const [imageEditorItem, setImageEditorItem] = React.useState(null);
  const resizeRef = React.useRef(null);

  // Get blocks inside this section
  const sectionBlocks = blocks.filter(b => b.parentSectionId === section.id);

  const handleDragStop = (e, d) => {
    updateSection(section.id, {
      x: grid.snapEnabled ? Math.round(d.x / grid.size) * grid.size : d.x,
      y: grid.snapEnabled ? Math.round(d.y / grid.size) * grid.size : d.y,
    });
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    updateSection(section.id, {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: grid.snapEnabled ? Math.round(position.x / grid.size) * grid.size : position.x,
      y: grid.snapEnabled ? Math.round(position.y / grid.size) * grid.size : position.y,
    });
  };

  const getSectionLabel = (type) => {
    const labels = {
      [sectionTypes.HEADER]: 'Header',
      [sectionTypes.GALLERY]: 'Gallery',
      [sectionTypes.DETAIL]: 'Detail',
      [sectionTypes.FOOTER]: 'Footer',
    };
    return labels[type] || type;
  };

  // Render Detail Section
  const renderDetailSection = () => {
    const detailText = section.styles?.detailText || 'เขียนรายละเอียดที่นี่...';
    const detailFont = section.styles?.detailFont || 'Sarabun';
    const detailSize = section.styles?.detailSize || 16;
    const detailColor = section.styles?.detailColor || '#414141';
    const detailAlign = section.styles?.detailAlign || 'left';
    const detailLineHeight = section.styles?.detailLineHeight || 1.8;

    return (
      <div
        className="detail-text-container"
        style={{
          width: '100%',
          height: '100%',
          padding: '20px',
          overflow: 'auto',
        }}
      >
        <div
          contentEditable={isSelected}
          suppressContentEditableWarning
          onBlur={(e) => {
            updateSection(section.id, {
              styles: {
                ...section.styles,
                detailText: e.target.innerText,
              },
            });
          }}
          style={{
            fontFamily: detailFont,
            fontSize: `${detailSize}px`,
            color: detailColor,
            textAlign: detailAlign,
            lineHeight: detailLineHeight,
            minHeight: '100%',
            outline: 'none',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
          }}
        >
          {detailText}
        </div>
      </div>
    );
  };

  // Render Footer Section
  const renderFooterLayout = () => {
    const footerText = section.styles?.footerText || '1129 PEA Contact Center';
    const footerTextAlign = section.styles?.footerTextAlign || 'center';
    const footerTextColor = section.styles?.footerTextColor || '#FFFFFF';
    const footerFontSize = section.styles?.footerFontSize || 18;
    const footerBgColor = section.styles?.backgroundColor || '#8D2F88';

    return (
      <div 
        className="footer-container"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: footerTextAlign === 'left' ? 'flex-start' : footerTextAlign === 'right' ? 'flex-end' : 'center',
          padding: '0 24px',
          backgroundColor: footerBgColor,
          color: footerTextColor,
          fontSize: `${footerFontSize}px`,
          fontFamily: 'Sarabun', // Default font
          fontWeight: 'bold',
        }}
      >
        {/* Phone Icon */}
        <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}><FaPhone /></span>
        <span
          contentEditable={isSelected}
          suppressContentEditableWarning
          onBlur={(e) => {
            updateSection(section.id, {
              styles: {
                ...section.styles,
                footerText: e.target.innerText,
              },
            });
          }}
          style={{
            outline: 'none',
            cursor: isSelected ? 'text' : 'default',
          }}
        >
          {footerText}
        </span>
      </div>
    );
  };

  const sectionStyle = {
    backgroundColor: section.styles?.backgroundColor || defaultSectionStyles[section.type]?.backgroundColor,
    borderColor: section.styles?.borderColor || defaultSectionStyles[section.type]?.borderColor,
    borderWidth: section.styles?.borderWidth || defaultSectionStyles[section.type]?.borderWidth,
    borderStyle: section.styles?.borderStyle || defaultSectionStyles[section.type]?.borderStyle,
  };

  // Render header layout guides
  const renderHeaderLayout = () => {
    if (section.type !== sectionTypes.HEADER) return null;
    
    const layout = section.styles?.layout || 'logo-text';
    const logoUrl = section.styles?.logoUrl || null;
    const title = section.styles?.title || 'หัวข้อโปสเตอร์';
    const organization = section.styles?.organization || 'สำนักงานใหญ่';
    const titleFont = section.styles?.titleFont || 'Kanit';
    const titleSize = section.styles?.titleSize || 50;
    const titleColor = section.styles?.titleColor || '#8D2F88';
    const orgFont = section.styles?.orgFont || 'Sarabun';
    const orgSize = section.styles?.orgSize || 30;
    const orgColor = section.styles?.orgColor || '#414141';
    
    return (
      <div className="header-layout-content">
        {layout === 'logo-text' && (
          <>
            <div className="header-logo-area" style={{ width: '20%' }}>
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="header-logo-img" />
              ) : (
                <div className="header-logo-placeholder">
                  <span className="slot-icon"><FaImage /></span>
                  <span className="slot-label">Logo</span>
                </div>
              )}
            </div>
            <div className="header-text-area" style={{ width: '80%' }}>
              <h1 className="header-title" style={{ fontFamily: titleFont, fontSize: `${titleSize}px`, color: titleColor }}>{title}</h1>
              <p className="header-org" style={{ fontFamily: orgFont, fontSize: `${orgSize}px`, color: orgColor }}>{organization}</p>
            </div>
          </>
        )}
        {layout === 'text-logo' && (
          <>
            <div className="header-text-area" style={{ width: '80%' }}>
              <h1 className="header-title" style={{ fontFamily: titleFont, fontSize: `${titleSize}px`, color: titleColor }}>{title}</h1>
              <p className="header-org" style={{ fontFamily: orgFont, fontSize: `${orgSize}px`, color: orgColor }}>{organization}</p>
            </div>
            <div className="header-logo-area" style={{ width: '20%' }}>
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="header-logo-img" />
              ) : (
                <div className="header-logo-placeholder">
                  <span className="slot-icon"><FaImage /></span>
                  <span className="slot-label">Logo</span>
                </div>
              )}
            </div>
          </>
        )}
        {layout === 'text-only' && (
          <div className="header-text-area" style={{ width: '100%' }}>
            <h1 className="header-title" style={{ fontFamily: titleFont, fontSize: `${titleSize}px`, color: titleColor }}>{title}</h1>
            <p className="header-org" style={{ fontFamily: orgFont, fontSize: `${orgSize}px`, color: orgColor }}>{organization}</p>
          </div>
        )}
        {layout === 'logo-only' && (
          <div className="header-logo-area" style={{ width: '100%' }}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="header-logo-img" />
            ) : (
              <div className="header-logo-placeholder">
                <span className="slot-icon"><FaImage /></span>
                <span className="slot-label">Logo</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Custom resize handlers
  const handleResizeStart = (itemId, direction, e) => {
    e.stopPropagation();
    const item = section.styles?.galleryItems?.find(i => i.id === itemId);
    if (!item) return;

    setResizing({
      itemId,
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: item.width,
      startHeight: item.height,
      startLeft: item.x,
      startTop: item.y,
    });
  };

  // Custom drag handlers
  const handleDragStart = (itemId, e) => {
    // Don't start drag if clicking on resize handle
    if (e.target.classList.contains('resize-handle')) return;
    
    // Don't start drag if it's a double click
    if (e.detail === 2) return;
    
    e.stopPropagation();
    const item = section.styles?.galleryItems?.find(i => i.id === itemId);
    if (!item) return;

    setDragging({
      itemId,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: item.x,
      startTop: item.y,
    });
  };

  // Resize effect
  React.useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const { itemId, direction, startX, startY, startWidth, startHeight, startLeft, startTop } = resizing;
      const deltaX = (e.clientX - startX) * gallerySettings.resizeSensitivity;
      const deltaY = (e.clientY - startY) * gallerySettings.resizeSensitivity;
      const { updateGalleryItem, updateSection } = useEditorStore.getState();
      const galleryItems = section.styles?.galleryItems || [];

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startLeft;
      let newY = startTop;

      // Calculate new dimensions based on direction
      if (direction.includes('e')) newWidth = Math.max(gallerySettings.minItemSize, startWidth + deltaX);
      if (direction.includes('w')) {
        newWidth = Math.max(gallerySettings.minItemSize, startWidth - deltaX);
        newX = startLeft + (startWidth - newWidth);
      }
      if (direction.includes('s')) newHeight = Math.max(gallerySettings.minItemSize, startHeight + deltaY);
      if (direction.includes('n')) {
        newHeight = Math.max(gallerySettings.minItemSize, startHeight - deltaY);
        newY = startTop + (startHeight - newHeight);
      }

      // Keep within bounds
      const maxWidth = gallerySettings.bounds.maxWidth;
      const maxHeight = gallerySettings.bounds.maxHeight;
      newWidth = Math.min(newWidth, maxWidth - newX);
      newHeight = Math.min(newHeight, maxHeight - newY);
      newX = Math.max(0, Math.min(newX, maxWidth - newWidth));
      newY = Math.max(0, Math.min(newY, maxHeight - newHeight));

      // Smart Auto-Layout: Check collision and auto-shrink overlapping items
      const currentItem = {
        id: itemId,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      };

      galleryItems.forEach((item) => {
        if (item.id === itemId) return; // Skip self

        // Check if items overlap
        const overlap = !(
          currentItem.x + currentItem.width <= item.x ||
          currentItem.x >= item.x + item.width ||
          currentItem.y + currentItem.height <= item.y ||
          currentItem.y >= item.y + item.height
        );

        if (overlap) {
          // Calculate overlap amounts
          const overlapRight = Math.max(0, (currentItem.x + currentItem.width) - item.x);
          const overlapLeft = Math.max(0, (item.x + item.width) - currentItem.x);
          const overlapBottom = Math.max(0, (currentItem.y + currentItem.height) - item.y);
          const overlapTop = Math.max(0, (item.y + item.height) - currentItem.y);

          let adjustedWidth = item.width;
          let adjustedHeight = item.height;
          let adjustedX = item.x;
          let adjustedY = item.y;

          // Shrink the overlapping item based on collision direction
          if (overlapBottom > 0 && overlapBottom < item.height) {
            // Current item pushing from top
            adjustedHeight = Math.max(gallerySettings.minItemSize, item.height - overlapBottom);
            adjustedY = item.y + (item.height - adjustedHeight);
          } else if (overlapTop > 0 && overlapTop < item.height) {
            // Current item pushing from bottom
            adjustedHeight = Math.max(gallerySettings.minItemSize, item.height - overlapTop);
          }

          if (overlapRight > 0 && overlapRight < item.width) {
            // Current item pushing from left
            adjustedWidth = Math.max(gallerySettings.minItemSize, item.width - overlapRight);
            adjustedX = item.x + (item.width - adjustedWidth);
          } else if (overlapLeft > 0 && overlapLeft < item.width) {
            // Current item pushing from right
            adjustedWidth = Math.max(gallerySettings.minItemSize, item.width - overlapLeft);
          }

          // Keep adjusted item within bounds
          adjustedWidth = Math.min(adjustedWidth, maxWidth - adjustedX);
          adjustedHeight = Math.min(adjustedHeight, maxHeight - adjustedY);
          adjustedX = Math.max(0, Math.min(adjustedX, maxWidth - adjustedWidth));
          adjustedY = Math.max(0, Math.min(adjustedY, maxHeight - adjustedHeight));

          // Update the overlapping item
          updateGalleryItem(section.id, item.id, {
            width: Math.round(adjustedWidth),
            height: Math.round(adjustedHeight),
            x: Math.round(adjustedX),
            y: Math.round(adjustedY),
          });
        }
      });

      // Update current item
      updateGalleryItem(section.id, itemId, {
        width: Math.round(newWidth),
        height: Math.round(newHeight),
        x: Math.round(newX),
        y: Math.round(newY),
      });
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, section.id]);

  // Drag effect
  React.useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e) => {
      const { itemId, startX, startY, startLeft, startTop } = dragging;
      const deltaX = (e.clientX - startX) * gallerySettings.dragSensitivity;
      const deltaY = (e.clientY - startY) * gallerySettings.dragSensitivity;
      const { updateGalleryItem } = useEditorStore.getState();
      const galleryItems = section.styles?.galleryItems || [];
      const item = galleryItems.find(i => i.id === itemId);
      if (!item) return;

      let newX = startLeft + deltaX;
      let newY = startTop + deltaY;

      // Keep within bounds
      const maxWidth = gallerySettings.bounds.maxWidth;
      const maxHeight = gallerySettings.bounds.maxHeight;
      newX = Math.max(0, Math.min(newX, maxWidth - item.width));
      newY = Math.max(0, Math.min(newY, maxHeight - item.height));

      // Smart collision detection during drag
      const currentItem = {
        id: itemId,
        x: newX,
        y: newY,
        width: item.width,
        height: item.height,
      };

      galleryItems.forEach((otherItem) => {
        if (otherItem.id === itemId) return; // Skip self

        // Check if items overlap
        const overlap = !(
          currentItem.x + currentItem.width <= otherItem.x ||
          currentItem.x >= otherItem.x + otherItem.width ||
          currentItem.y + currentItem.height <= otherItem.y ||
          currentItem.y >= otherItem.y + otherItem.height
        );

        if (overlap) {
          // Calculate which direction to push/shrink
          const overlapRight = Math.max(0, (currentItem.x + currentItem.width) - otherItem.x);
          const overlapLeft = Math.max(0, (otherItem.x + otherItem.width) - currentItem.x);
          const overlapBottom = Math.max(0, (currentItem.y + currentItem.height) - otherItem.y);
          const overlapTop = Math.max(0, (otherItem.y + otherItem.height) - currentItem.y);

          let adjustedX = otherItem.x;
          let adjustedY = otherItem.y;
          let adjustedWidth = otherItem.width;
          let adjustedHeight = otherItem.height;

          // Determine primary collision direction
          const horizontalOverlap = Math.min(overlapRight, overlapLeft);
          const verticalOverlap = Math.min(overlapBottom, overlapTop);

          if (verticalOverlap < horizontalOverlap) {
            // Vertical collision is stronger - push vertically
            if (overlapBottom > 0 && overlapBottom <= verticalOverlap) {
              // Push down
              adjustedY = currentItem.y + currentItem.height;
              if (adjustedY + adjustedHeight > maxHeight) {
                adjustedHeight = Math.max(gallerySettings.minItemSize, maxHeight - adjustedY);
              }
            } else if (overlapTop > 0) {
              // Push up
              const newBottom = currentItem.y;
              adjustedHeight = Math.max(gallerySettings.minItemSize, newBottom - adjustedY);
            }
          } else {
            // Horizontal collision is stronger - push horizontally
            if (overlapRight > 0 && overlapRight <= horizontalOverlap) {
              // Push right
              adjustedX = currentItem.x + currentItem.width;
              if (adjustedX + adjustedWidth > maxWidth) {
                adjustedWidth = Math.max(gallerySettings.minItemSize, maxWidth - adjustedX);
              }
            } else if (overlapLeft > 0) {
              // Push left
              const newRight = currentItem.x;
              adjustedWidth = Math.max(gallerySettings.minItemSize, newRight - adjustedX);
            }
          }

          // Keep adjusted item within bounds
          adjustedX = Math.max(0, Math.min(adjustedX, maxWidth - adjustedWidth));
          adjustedY = Math.max(0, Math.min(adjustedY, maxHeight - adjustedHeight));

          // Update the overlapping item
          updateGalleryItem(section.id, otherItem.id, {
            x: Math.round(adjustedX),
            y: Math.round(adjustedY),
            width: Math.round(adjustedWidth),
            height: Math.round(adjustedHeight),
          });
        }
      });

      // Update current item
      updateGalleryItem(section.id, itemId, {
        x: Math.round(newX),
        y: Math.round(newY),
      });
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, section.id]);

  // Render gallery layout
  const renderGalleryLayout = () => {
    if (section.type !== sectionTypes.GALLERY) return null;
    
    const galleryItems = section.styles?.galleryItems || [];
    const galleryGrid = section.styles?.galleryGrid || { enabled: true, size: 20, gap: 0, color: 'rgba(141, 47, 136, 0.1)' };
    const selectedLayout = section.styles?.selectedLayout || null;
    const galleryTitle = section.styles?.galleryTitle || 'หัวข้อย่อย';
    const borderRadius = section.styles?.galleryBorderRadius || 0;
    const borderWidth = section.styles?.galleryBorderWidth || 0;
    const borderColor = section.styles?.galleryBorderColor || '#8D2F88';
    
    // Handle image upload
    const handleImageUpload = (itemId) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            updateGalleryItem(section.id, itemId, { imageUrl: event.target.result });
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    };
    
    const handleItemDragStop = (itemId, e, d) => {
      const item = galleryItems.find(i => i.id === itemId);
      
      let finalX = d.x;
      let finalY = d.y;
      
      // Keep within bounds only (no gap enforcement)
      finalX = Math.max(0, Math.min(finalX, section.width - item.width));
      finalY = Math.max(0, Math.min(finalY, section.height - item.height));
      
      // Apply final position directly
      updateGalleryItem(section.id, itemId, { x: finalX, y: finalY });
    };
    
    const handleItemResizeStop = (itemId, e, direction, ref, delta, position) => {
      updateGalleryItem(section.id, itemId, {
        width: parseInt(ref.style.width),
        height: parseInt(ref.style.height),
        x: position.x,
        y: position.y,
      });
    };
    
    return (
      <div className="gallery-layout-content">
        {/* Gallery Section Header */}
        <div className="gallery-section-header">
          <h2 className="gallery-section-title">{galleryTitle}</h2>
        </div>

        {/* Gallery Grid Area */}
        <div className="gallery-grid-area">
          {galleryItems.length === 0 && !selectedLayout ? (
            <div className="gallery-empty-state">
              <span className="gallery-icon"><FaPalette /></span>
              <p>เลือก Layout จาก Properties Panel</p>
            </div>
          ) : (
            <div className="gallery-items-wrapper">
              {galleryGrid.enabled && (
                <div 
                  className="gallery-grid-overlay"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, ${galleryGrid.color || 'rgba(141, 47, 136, 0.1)'} 1px, transparent 1px),
                      linear-gradient(to bottom, ${galleryGrid.color || 'rgba(141, 47, 136, 0.1)'} 1px, transparent 1px)
                    `,
                    backgroundSize: `${galleryGrid.size}px ${galleryGrid.size}px`,
                  }}
                />
              )}
              {galleryItems.map((item) => (
                <div
                  key={item.id}
                  className={`gallery-item ${selectedItemId === item.id ? 'gallery-item-selected' : ''} ${!item.imageUrl ? 'gallery-item-empty' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItemId(item.id);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    
                    if (item.imageUrl) {
                      // ถ้ามีภาพแล้ว → เปิด Modal
                      console.log('Opening modal for item:', item.id);
                      setImageEditorItem(item);
                    } else {
                      // ถ้ายังไม่มีภาพ → Upload
                      console.log('Opening upload for item:', item.id);
                      handleImageUpload(item.id);
                    }
                  }}
                  onMouseDown={(e) => {
                    // Only start drag on single click
                    if (e.detail === 1) {
                      handleDragStart(item.id, e);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    left: `${Math.round(item.x)}px`,
                    top: `${Math.round(item.y)}px`,
                    width: `${Math.round(item.width)}px`,
                    height: `${Math.round(item.height)}px`,
                    cursor: dragging?.itemId === item.id ? 'grabbing' : 'grab',
                    // Apply frame styles if selected, otherwise fallback to section defaults
                    ...(item.frameStyle ? frameStyles.find(f => f.id === item.frameStyle)?.style : {
                      borderRadius: `${borderRadius}px`,
                      border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
                    }),
                    overflow: 'hidden',
                  }}
                >
                  {item.imageUrl ? (
                    <img 
                      src={item.imageUrl} 
                      alt={item.caption || 'Gallery image'} 
                      className="gallery-item-img"
                      style={{
                        objectFit: item.objectFit || 'cover',
                        objectPosition: `${50 + (item.offsetX || 0)}% ${50 + (item.offsetY || 0)}%`,
                        transform: `scale(${(item.zoom || 100) / 100}) rotate(${item.rotation || 0}deg)`,
                        filter: `brightness(${item.brightness || 100}%) contrast(${item.contrast || 100}%) blur(${item.blur || 0}px) saturate(${item.saturation || 100}%) grayscale(${item.grayscale || 0}%) sepia(${item.sepia || 0}%) hue-rotate(${item.hueRotate || 0}deg)`,
                        // If frame style is applied to container, we might not need border radius here, or we match it
                        borderRadius: item.frameStyle ? (frameStyles.find(f => f.id === item.frameStyle)?.style.borderRadius || '0') : `${borderRadius}px`,
                      }}
                    />
                  ) : (
                    <div className="gallery-item-placeholder">
                      <span className="placeholder-icon"><FaCamera /></span>
                      <span className="placeholder-text">Click to Upload</span>
                    </div>
                  )}
                  {item.caption && (
                    <div className="gallery-item-caption">{item.caption}</div>
                  )}
                  
                  {/* Resize Handles */}
                  {selectedItemId === item.id && (
                    <>
                      <div className="resize-handle resize-handle-nw" onMouseDown={(e) => handleResizeStart(item.id, 'nw', e)} />
                      <div className="resize-handle resize-handle-ne" onMouseDown={(e) => handleResizeStart(item.id, 'ne', e)} />
                      <div className="resize-handle resize-handle-sw" onMouseDown={(e) => handleResizeStart(item.id, 'sw', e)} />
                      <div className="resize-handle resize-handle-se" onMouseDown={(e) => handleResizeStart(item.id, 'se', e)} />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Rnd
        position={{ x: Math.round(section.x), y: Math.round(section.y) }}
        size={{ width: Math.round(section.width), height: Math.round(section.height) }}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        dragGrid={grid.snapEnabled ? [grid.size, grid.size] : [1, 1]}
        resizeGrid={grid.snapEnabled ? [grid.size, grid.size] : [1, 1]}
        bounds="parent"
        onClick={() => setSelectedSection(section)}
        className={`section ${isSelected ? 'selected' : ''} ${showSectionOverlay ? 'show-overlay' : ''}`}
        style={{
          zIndex: section.zIndex || 1,
          margin: 0,
          padding: 0,
          ...sectionStyle,
        }}
        data-section-type={section.type}
        disableDragging={true}
        enableResizing={false}
      >


        {showSectionOverlay && (
          <div className={`section-overlay section-overlay-${section.type}`}>
            <span className="section-overlay-label">{getSectionLabel(section.type)}</span>
          </div>
        )}
        <div className="section-label">
          {getSectionLabel(section.type)}
        </div>
        <div className="section-content">
          {section.type === sectionTypes.HEADER ? (
            renderHeaderLayout()
          ) : section.type === sectionTypes.GALLERY ? (
            renderGalleryLayout()
          ) : section.type === sectionTypes.DETAIL ? (
            renderDetailSection()
          ) : section.type === sectionTypes.FOOTER ? (
            renderFooterLayout()
          ) : (
            <div className="drop-zone-hint">
              ลากวาง Logo, Text, Image ลงที่นี่
            </div>
          )}
        </div>
      </Rnd>
      
      {/* Image Editor Modal - Outside Rnd to avoid z-index stacking context */}
      {imageEditorItem && (
        <ImageEditorModal
          item={imageEditorItem}
          sectionId={section.id}
          onClose={() => setImageEditorItem(null)}
          onUpdate={updateGalleryItem}
        />
      )}
    </>
  );
};

export default Section;
